import type { ChatProvider } from '#types/plugin/chat'

declare global {
  interface Window {
    sierra: {
      clearPersistedConversation: () => void
      closeChatModal: () => void
      openChatModal: () => void
      updateVariables: (variables: { [name: string]: any }) => void
    }
    sierraConfig: {
      customGreeting?: string
      onConversationStart?: () => void
      onClose?: () => void
      onOpen?: (opts: { isNewConversation: boolean }) => void
      variables?: {
        consumerId?: string
        isLoyaltyMember?: boolean
        locale: string
      }
    }
  }
}

export default (): ChatProvider => {
  const { $feature, $gtm, $locale, $t } = useNuxtApp()
  const auth = useAuthStore()
  const user = useUserStore()
  const { details, isLoyaltyMember } = storeToRefs(user)
  const { embedUrl } = $feature.configLivechat
  const isAvailable = ref(false)

  window.sierraConfig = {}

  const updateSierraWithUserData = (profileDetails) => {
    if (!profileDetails) return
    const userData = {
      variables: {
        consumerId: details.value?.consumerId,
        isLoyaltyMember: isLoyaltyMember.value,
        locale: $locale
      }
    }
    Object.assign(window.sierraConfig, userData)
    if (window.sierra && userData.variables)
      window.sierra.updateVariables(userData.variables)

    const firstName = profileDetails.firstName || ''
    if (auth.loggedIn) {
      if (isLoyaltyMember.value) {
        window.sierraConfig.customGreeting = replaceAll($t.sierraChatGreetingLoggedInAndLoyalty, {
          brandName: $t.brandName,
          firstName
        })
      }
      else {
        window.sierraConfig.customGreeting = replaceAll($t.sierraChatGreetingLoggedInAndNotLoyalty, {
          firstName
        })
      }
    }
    else {
      window.sierraConfig.customGreeting = $t.sierraChatGreeting
    }
  }

  const setSierraConfig = () => {
    const sierraConfig = {
      canEndConversation: true,
      chatInterfaceStrings: {
        disclosure: $t.sierraChatDisclaimer
      },
      customGreeting: $t.sierraChatGreeting,
      customStyle: { zIndex: '9999' },
      dimensions: {
        width: '25rem',
        height: '40rem'
      },
      display: 'corner'
    }
    Object.assign(window.sierraConfig, sierraConfig)
  }

  const initializeLiveChatEventTracking = () => {
    window.sierraConfig.onOpen = () => $gtm?.push('livechat.onAction', 'Displayed', 'Sierra')
    window.sierraConfig.onConversationStart = () => {
      $gtm?.push('livechat.onAction', 'Started', 'Sierra')
      $gtm?.push('livechat.onAction', 'Agent Started', 'Sierra')
    }
    window.sierraConfig.onClose = () => $gtm?.push('livechat.onAction', 'Closed', 'Sierra')
  }

  const setSierraChat = () => {
    const isCookieAccepted = hasCookieConsent(['required', 'performanceAndAnalytics', 'functional', 'targeting'])
    try {
      if (isCookieAccepted) {
        setSierraConfig()
        updateSierraWithUserData(details.value)
        loadScript(embedUrl!, { type: 'module' })
        initializeLiveChatEventTracking()
        isAvailable.value = true
      }
    }
    catch (err) {
      console.error('Failed to load Sierra chat script:', err)
      isAvailable.value = false
    }
  }

  const openChat = () => {
    if (window.sierra) {
      window.sierra.openChatModal()
    }
  }

  const closeChat = () => {
    if (window.sierra) {
      window.sierra.clearPersistedConversation()
      window.sierra.closeChatModal()
    }
  }

  watch([() => auth.loggedIn, () => details.value], ([newAuthState, newDetails], [oldAuthState, oldDetails]) => {
    // Only completely reset if login state changes
    if (newAuthState !== oldAuthState) {
      if (newAuthState) {
        updateSierraWithUserData(newDetails)
      }
      else {
        window.sierraConfig.customGreeting = $t.sierraChatGreeting
        delete window.sierraConfig.variables
        window.sierraConfig.variables = {
          locale: $locale
        }
      }
      closeChat()
      setSierraChat()
    }
    // If just details changed but user was already logged in, update variables without closing the chat
    if (newAuthState && newDetails !== oldDetails) {
      updateSierraWithUserData(newDetails)
      // Update variables without closing if chat is open
      if (window.sierra && window.sierraConfig.variables)
        window.sierra.updateVariables(window.sierraConfig.variables)
    }
  }, { immediate: true })

  setSierraChat()

  return {
    provide: {
      chat: {
        open: openChat,
        available: isAvailable
      }
    }
  }
}
